/**
 * useBeanActions
 *
 * Call these helpers from within existing pages to award XP and update streaks.
 * Each action is automatically deduped to once per calendar day per source.
 *
 * Usage:
 *   const { onDailyLogSubmitted, onGuideArticleOpened, onMedLogged } = useBeanActions();
 */
import { useCallback } from 'react';
import { useBean } from '../context/BeanContext';

export function useBeanActions() {
  const { awardXP, checkStreak, rollbackStreak } = useBean();

  /**
   * Call after vitals are successfully submitted.
   * Kept for backward-compatibility but no longer fires streak/XP on its own —
   * use `onTodayCardComplete` instead (triggered when all 5 steps are done).
   */
  const onDailyLogSubmitted = useCallback(async (_period?: 'AM' | 'PM') => {
    // no-op: streak is now driven by onTodayCardComplete
  }, []);

  /**
   * Call when all 5 Today's Card steps are complete for the first time today.
   * Guarded internally by `checkStreak` (once per calendar day) and
   * `awardXP` dedup, so safe to call multiple times.
   */
  const onTodayCardComplete = useCallback(async () => {
    await checkStreak();
    await awardXP('daily_log');
  }, [awardXP, checkStreak]);

  /** Call when a guide article page is opened/viewed */
  const onGuideArticleOpened = useCallback(async () => {
    await awardXP('guide_article');
  }, [awardXP]);

  /** Call after a medication log/reminder is acknowledged */
  const onMedLogged = useCallback(async () => {
    await awardXP('med_log');
  }, [awardXP]);

  return {
    onDailyLogSubmitted,
    onTodayCardComplete,
    onGuideArticleOpened,
    onMedLogged,
    rollbackStreak,
  };
}
